import React, { useEffect, useState, useContext, useMemo } from "react";
import { Row, Col, Typography, Pagination, Button, message } from "antd";
import { useNavigate } from "react-router-dom";
import { IconFlame, IconMapPin, IconHeart, IconHeartFilled, IconShoppingCart } from "@tabler/icons-react";
import Navbar from "./Navbar";
import Footer from "../component/Footer";
import ProductCardSkeleton from "../component/skeletons/ProductCardSkeleton";
import DealServ from "../service/DealServ";
import { LocationContext } from "../LocationContext";
import { usePreference } from "../context/PreferenceContext";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";
import SafeImage from "../component/SafeImage";
import { CartContext } from "../CartContext";
import { WishlistContext } from "../WishlistContext";
import AuthServ from "../service/AuthServ";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import { formatUnit } from "../util/unitFormatter";

const { Title, Text } = Typography;

const DealCard = ({ product, isFeatured = false, formatETA }) => {
    const { t, formatPrice } = usePreference();
    const navigate = useNavigate();
    const { addToCart } = useContext(CartContext);
    const { toggleWishlist, isInWishlist } = useContext(WishlistContext);
    const { showAlert } = useGlobalAuthModal();

    const productId = product._id || product.id;
    const shopId = product.shopId?._id || product.shopId;

    // Resolve variant logic
    const allVariants = useMemo(() => {
        let variants = [];
        if (product.options && product.options.length > 0) {
            product.options.forEach(opt => {
                if (opt.variants) variants.push(...opt.variants);
            });
        } else if (product.hasVariants && product.variants) {
            variants = product.variants;
        }
        return [...variants].sort((a, b) => (a.price || 0) - (b.price || 0));
    }, [product]);

    const currentVariant = useMemo(() => {
        if (!allVariants.length) return null;
        const dealMatch = allVariants.find(v => v.isHotDeal === true);
        if (dealMatch) return dealMatch;
        return allVariants[0];
    }, [allVariants]);

    // Format unit helpers
    const formatUnit = (val, type) => {
        if (!val) return "";
        return `${val} ${type === "weight" ? "g" : ""}`;
    };

    const variantPills = useMemo(() => {
        if (!allVariants || allVariants.length === 0) return null;
        const activeVal = currentVariant ? (currentVariant.val || currentVariant.value || currentVariant.name) : null;
        if (!activeVal) return null;
        const remainingCount = allVariants.length - 1;
        return (
            <div className="deal-variant-pills-row" style={{ display: 'flex', gap: '6px', marginTop: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span className="deal-variant-pill" style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '3px 8px',
                    borderRadius: '9999px',
                    border: '1px solid #e2e8f0'
                }}>
                    {activeVal}
                </span>
                {remainingCount > 0 && (
                    <span className="deal-variant-pill-more" style={{
                        background: '#f8fafc',
                        color: '#64748b',
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '3px 8px',
                        borderRadius: '9999px',
                        border: '1px solid #cbd5e1'
                    }}>
                        +{remainingCount} more
                    </span>
                )}
            </div>
        );
    }, [allVariants, currentVariant]);

    const displayPrice = currentVariant ? (currentVariant.price || currentVariant.pri) : product.price;
    const displayMrp = currentVariant ? (currentVariant.mrp || product.mrp) : product.mrp;
    const displayImage = product.imageUrl || (product.images && product.images.length > 0 ? product.images[0] : null);

    // Calculate discount percentage
    const discount = useMemo(() => {
        if (displayMrp > displayPrice) {
            return Math.round(((displayMrp - displayPrice) / displayMrp) * 100);
        }
        return 0;
    }, [displayPrice, displayMrp]);

    const handleAddToCart = async (e) => {
        e.stopPropagation();
        const options = {};
        if (currentVariant) {
            const val = currentVariant.val || currentVariant.value || currentVariant.name || currentVariant.size || "Default";
            const group = product.options?.find(o => o.variants?.some(v => String(v._id || v.val || v.value || v.name || v.size) === String(currentVariant._id || val)));
            if (group) options[group.name] = val;
            else if (product.variantType) options[product.variantType] = val;
            else options["Type"] = val;
        }
        await addToCart(productId, 1, options, {
            name: product.name,
            image: displayImage,
            variant: currentVariant ? formatUnit(currentVariant.val || currentVariant.value, product.variantType || "Option") : null,
            price: displayPrice,
            mrp: displayMrp,
            shopId: shopId
        });
        message.success(`${product.name} added to cart`);
    };

    const handleWishlist = (e) => {
        e.stopPropagation();
        if (!AuthServ.isLogged()) {
            showAlert({
                title: t("login_required"),
                description: t("login_to_add_to_cart_desc"),
                buttons: [
                    { text: t("cancel"), variant: "default" },
                    { text: t("login"), variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }
        toggleWishlist(productId);
    };

    const goToProduct = () => {
        const queryParams = new URLSearchParams();
        if (product.price) queryParams.set("dealPrice", product.price);
        if (currentVariant?._id || currentVariant?.value) {
            queryParams.set("variantId", currentVariant?._id || currentVariant?.value);
        }
        const url = `/single/Products/${productId}?${queryParams.toString()}`;
        window.open(url, '_blank');
    };

    const isFav = isInWishlist ? isInWishlist(productId) : false;

    // Delivery Estimate Label
    const etaText = useMemo(() => {
        const est = formatETA ? formatETA(shopId, product.deliveryTime) : null;
        if (est) return est;
        if (product.deliveryTime) return `Delivery by ${product.deliveryTime}`;
        return "Delivery in minutes";
    }, [formatETA, shopId, product.deliveryTime]);

    if (isFeatured) {
        return (
            <div className="deal-featured-card" onClick={goToProduct}>
                <div className="deal-featured-img-container">
                    <SafeImage src={displayImage} alt={product.name} className="deal-featured-img" />
                    {discount > 0 && (
                        <span className="deal-badge-discount" style={{ zIndex: 10 }}>{discount}% OFF</span>
                    )}
                    <button className="deal-card-wishlist" onClick={handleWishlist} style={{ zIndex: 10 }}>
                        {isFav ? <IconHeartFilled size={18} color="#ef4444" /> : <IconHeart size={18} color="#94a3b8" />}
                    </button>
                </div>
                
                <div className="deal-featured-content">
                    <div>
                        <div className="deal-delivery-badge">
                            <span className="deal-thunderbolt">⚡</span> {etaText}
                        </div>

                        <h2 className="deal-featured-title" style={{ marginTop: '4px' }}>{product.name}</h2>
                        
                        <div className="deal-card-price-row" style={{ marginTop: '8px', marginBottom: '8px' }}>
                            <span className="deal-card-price" style={{ fontSize: '20px' }}>{formatPrice(displayPrice)}</span>
                            {displayMrp > displayPrice && (
                                <span className="deal-card-mrp" style={{ fontSize: '13px' }}>{formatPrice(displayMrp)}</span>
                            )}
                        </div>
                        
                        {/* Variant Pills Row */}
                        {variantPills}
                    </div>

                    <div className="deal-card-footer" style={{ borderTop: 'none', paddingTop: 0, marginTop: '12px' }}>
                        <span className="deal-card-shop" style={{ display: 'flex', alignItems: 'center', gap: '4px', maxWidth: '140px' }}>
                            🏬 {product.shopId?.name || product.shop?.name || "Local Shop"}
                        </span>
                        <button className="deal-card-add-btn" onClick={handleAddToCart}>ADD</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="deal-regular-card" onClick={goToProduct}>
            <div className="deal-card-img-wrapper">
                <SafeImage src={displayImage} alt={product.name} className="deal-card-img" />
                {discount > 0 && (
                    <span className="deal-badge-discount">{discount}% OFF</span>
                )}
                <button className="deal-card-wishlist" onClick={handleWishlist}>
                    {isFav ? <IconHeartFilled size={18} color="#ef4444" /> : <IconHeart size={18} color="#94a3b8" />}
                </button>
            </div>
            
            <div className="deal-card-body">
                <div className="deal-card-delivery">
                    <span className="deal-thunderbolt">⚡</span> {etaText}
                </div>
                
                <h3 className="deal-card-title">{product.name}</h3>
                
                <div className="deal-card-price-row">
                    <span className="deal-card-price">{formatPrice(displayPrice)}</span>
                    {displayMrp > displayPrice && (
                        <span className="deal-card-mrp">{formatPrice(displayMrp)}</span>
                    )}
                </div>
                
                {/* Variant Pills Row */}
                {variantPills}
                
                <div className="deal-card-footer">
                    <span className="deal-card-shop" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        🏬 {product.shopId?.name || product.shop?.name || "Local Shop"}
                    </span>
                    <button className="deal-card-add-btn" onClick={handleAddToCart}>ADD</button>
                </div>
            </div>
        </div>
    );
};

const Deals = () => {
    const { t } = usePreference();
    const navigate = useNavigate();

    const { latitude, longitude, radius } = useContext(LocationContext);

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(24);
    const [selectedCategory, setSelectedCategory] = useState("All");



    // Collect unique shopIds from loaded deal products
    const shopIdsForEstimate = useMemo(() => {
        const ids = products
            .map(p => p.shopId?._id?.toString() || p.shopId?.toString())
            .filter(Boolean);
        return [...new Set(ids)];
    }, [products]);

    // ONE shared delivery estimate for all deal cards
    const { formatETA } = useDeliveryEstimate(shopIdsForEstimate, latitude, longitude);



    useEffect(() => {
        fetchDeals();
        window.scrollTo(0, 0);
    }, [currentPage, pageSize, latitude, longitude, radius]);

    const fetchDeals = async () => {
        try {
            setLoading(true);
            const params = {
                skip: (currentPage - 1) * pageSize,
                limit: pageSize,
                lat: latitude,
                lng: longitude,
                radius: radius,
                type: "today"
            };

            const res = await DealServ.index(params);
            const rawDeals = res.data || [];

            const dealsList = rawDeals
                .filter(deal => deal.product && deal.product.isActive !== false)
                .map(deal => ({
                    ...deal.product,
                    price: deal.dealPrice || deal.product.price,
                    _id: deal.product._id || deal.product.id
                }));

            const filteredDeals = dealsList.filter(p => {
                const mrp = p.mrp || 0;
                const price = p.price || 0;
                return mrp > price || (p.discount && p.discount > 0);
            });

            setProducts(filteredDeals);
            setTotal(res.total || filteredDeals.length);
        } catch (err) {
            console.error("Error fetching deals:", err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Extract categories list dynamically from loaded products
    const categories = useMemo(() => {
        const cats = ["All"];
        products.forEach(p => {
            const catName = p.categoryId?.name || p.category;
            if (catName && !cats.includes(catName)) {
                cats.push(catName);
            }
        });
        return cats;
    }, [products]);

    const displayedProducts = useMemo(() => {
        if (selectedCategory === "All") return products;
        return products.filter(p => (p.categoryId?.name || p.category) === selectedCategory);
    }, [products, selectedCategory]);

    return (
        <>
            <Navbar />
            <div className="deals-page standard-page-container pcu-padding-bottom-60">
                
                {/* ── Category Pill Tabs Navigation ── */}
                {!loading && products.length > 0 && (
                    <div className="deals-category-pills-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <div className="deals-category-icon-badge" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#ffedd5',
                            color: '#ea580c',
                            padding: '8px 12px',
                            borderRadius: '9999px',
                            fontWeight: '700',
                            fontSize: '13px',
                            border: '1px solid rgba(234, 88, 12, 0.15)',
                            gap: '6px',
                            whiteSpace: 'nowrap'
                        }}>
                            <IconFlame size={16} />
                            <span>Today's Deals</span>
                        </div>
                        <div className="deals-category-pills" style={{ flex: 1, marginBottom: 0, padding: '4px 0' }}>
                            {categories.map((catName) => (
                                <button
                                    key={catName}
                                    className={`deals-pill-btn ${selectedCategory === catName ? "active" : ""}`}
                                    onClick={() => setSelectedCategory(catName)}
                                >
                                    {catName === "All" ? "All Deals" : catName}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Premium Hero Banner Section ── */}
                {!loading && products.length > 0 && (
                    <div className="deal-hero-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                        <div className="deal-hero-text" style={{ flex: 1 }}>
                            <span className="deal-hero-tag">🔥 TODAY'S HOT DEALS</span>
                            <h1 className="deal-hero-title">Unbeatable Prices Delivered in Minutes</h1>
                            <p className="deal-hero-subtitle">
                                Exclusive discounts on everyday essentials just for you. Grab these hyper-local deals before they're gone!
                            </p>
                            
                            <div className="deal-features-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <div className="deal-feature-badge">
                                    <div className="deal-feature-icon-circle">🏷️</div>
                                    Best Prices
                                </div>
                                <span className="deal-feature-plus" style={{ color: '#fdba74', fontWeight: 'bold' }}>+</span>
                                <div className="deal-feature-badge">
                                    <div className="deal-feature-icon-circle">⚡</div>
                                    10 Min Delivery
                                </div>
                                <span className="deal-feature-plus" style={{ color: '#fdba74', fontWeight: 'bold' }}>+</span>
                                <div className="deal-feature-badge">
                                    <div className="deal-feature-icon-circle">🏬</div>
                                    Local Stock
                                </div>
                            </div>
                        </div>
                        
                        {/* 3D Shopping Bag Illustration */}
                        <div className="deal-hero-illustration" style={{ flex: '0.4', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <img 
                                src="/deals_banner_illustration.jpg" 
                                alt="Shopping Bag" 
                                style={{ 
                                    maxHeight: '140px', 
                                    objectFit: 'contain',
                                    filter: 'drop-shadow(0 10px 20px rgba(251, 146, 60, 0.15))'
                                }} 
                            />
                        </div>
                    </div>
                )}

                {loading ? (
                    <Row gutter={[16, 24]}>
                        {[...Array(8)].map((_, i) => (
                            <Col xs={12} sm={8} md={6} lg={4} xl={3} key={i}>
                                <ProductCardSkeleton />
                            </Col>
                        ))}
                    </Row>
                ) : displayedProducts.length === 0 ? (
                    <div 
                        className="empty-state-container pcu-padding-y-100"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            border: 'none',
                            boxShadow: 'none',
                            background: 'transparent'
                        }}
                    >
                        <IconMapPin size={48} color="#cbd5e1" className="no-results-icon" style={{ marginBottom: '16px' }} />
                        <Title level={4} className="pcu-color-muted pcu-margin-top-16 pcu-margin-bottom-8">
                            No deals available in this category
                        </Title>
                        <Text type="secondary" className="empty-state-subtitle">
                            Please select a different category or location to view deals and offers.
                        </Text>
                    </div>
                ) : (
                    <>
                        {/* Deals Grid Layout */}
                        <Row gutter={[16, 24]}>
                            {displayedProducts.map((product, idx) => {
                                // 6-product repeating layout cycle:
                                // Row 1: Left Featured (idx % 6 === 0), Right Regular (idx % 6 === 1, 2)
                                // Row 2: Left Regular (idx % 6 === 3, 4), Right Featured (idx % 6 === 5)
                                const cycleIdx = idx % 6;
                                const isFeatured = (cycleIdx === 0 || cycleIdx === 5);
                                
                                const colSpan = isFeatured 
                                    ? { xs: 24, sm: 24, md: 24, lg: 12, xl: 12 } 
                                    : { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 };
                                
                                // Responsive order: ensure Featured card stacks on top on mobile/tablet for Row 2
                                let colOrder = undefined;
                                if (cycleIdx === 3 || cycleIdx === 4) {
                                    colOrder = { xs: 2, sm: 2, md: 2, lg: 1 };
                                } else if (cycleIdx === 5) {
                                    colOrder = { xs: 1, sm: 1, md: 1, lg: 2 };
                                }
                                
                                return (
                                    <Col key={`${product._id}-${idx}`} {...colSpan} order={colOrder} style={{ display: 'flex' }}>
                                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <DealCard product={product} isFeatured={isFeatured} formatETA={formatETA} />
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>

                        {total > pageSize && (
                            <div className="pagination-container pcu-margin-top-40 pcu-flex pcu-justify-center">
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={total}
                                    onChange={(page, size) => {
                                        setCurrentPage(page);
                                        setPageSize(size);
                                    }}
                                    showSizeChanger={false}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <Footer />

            <style>{`
                .deals-page {
                    min-height: 80vh;
                    padding-top: 20px;
                }
                .pagination-container .ant-pagination-item-active {
                    border-color: #ea580c;
                }
                .pagination-container .ant-pagination-item-active a {
                    color: #ea580c;
                }

                /* ── Hero Banner Styles ── */
                .deal-hero-section {
                    background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
                    border-radius: 16px;
                    padding: 20px 24px;
                    box-shadow: 0 8px 24px rgba(251, 146, 60, 0.06);
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    margin-bottom: 20px;
                    gap: 24px;
                    position: relative;
                    border: 1px solid rgba(253, 186, 116, 0.25);
                }
                .deal-hero-text {
                    flex: 1;
                }
                .deal-hero-tag {
                    background: #ffedd5;
                    color: #ea580c;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 4px 10px;
                    border-radius: 9999px;
                    letter-spacing: 0.5px;
                    display: inline-block;
                    margin-bottom: 8px;
                    border: 1px solid rgba(234, 88, 12, 0.15);
                }
                .deal-hero-title {
                    font-size: 24px;
                    font-weight: 800;
                    color: #1e293b;
                    line-height: 1.25;
                    margin-bottom: 8px;
                }
                .deal-hero-subtitle {
                    font-size: 13px;
                    color: #64748b;
                    line-height: 1.5;
                    margin-bottom: 16px;
                    max-width: 600px;
                }
                .deal-features-row {
                    display: flex;
                    gap: 20px;
                    flex-wrap: wrap;
                }
                .deal-feature-badge {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #4b5563;
                }
                .deal-feature-icon-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: #ffedd5;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    color: #ea580c;
                    border: 1px solid rgba(234, 88, 12, 0.1);
                }

                /* ── Countdown Timer Box ── */
                .deal-timer-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-width: 200px;
                }
                .deal-timer-label {
                    color: #ef4444;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.5px;
                    margin-bottom: 12px;
                    text-transform: uppercase;
                }
                .deal-timer-digits {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .deal-timer-block {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .deal-timer-box {
                    background: #f8fafc;
                    border-radius: 8px;
                    width: 44px;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    font-weight: 800;
                    color: #0f172a;
                    border: 1px solid #e2e8f0;
                }
                .deal-timer-sublabel {
                    font-size: 8px;
                    font-weight: 800;
                    color: #94a3b8;
                    text-transform: uppercase;
                    margin-top: 4px;
                }
                .deal-timer-separator {
                    font-size: 18px;
                    font-weight: 800;
                    color: #cbd5e1;
                    align-self: flex-start;
                    margin-top: 10px;
                }

                /* ── Category Pill Tabs ── */
                .deals-category-pills {
                    display: flex;
                    gap: 8px;
                    overflow-x: auto;
                    padding: 12px 4px;
                    margin-bottom: 24px;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                }
                .deals-category-pills::-webkit-scrollbar {
                    display: none;
                }
                .deals-pill-btn {
                    background: #ffffff;
                    color: #475569;
                    border: 1px solid #e2e8f0;
                    border-radius: 9999px;
                    padding: 8px 18px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: all 0.2s;
                }
                .deals-pill-btn:hover {
                    border-color: #ea580c;
                    color: #ea580c;
                }
                .deals-pill-btn.active {
                    background: #ea580c;
                    color: #ffffff;
                    border-color: #ea580c;
                    font-weight: 700;
                    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.2);
                }

                /* ── Custom Cards Styling ── */
                .deal-featured-card {
                    display: flex;
                    flex-direction: row;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f1f5f9;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.02);
                    padding: 24px;
                    gap: 24px;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                    height: 100%;
                    min-height: 280px;
                    position: relative;
                }
                .deal-featured-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                }
                .deal-featured-content {
                    flex: 1.2;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }
                .deal-featured-img-container {
                    flex: 0.8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    border-radius: 12px;
                    overflow: hidden;
                    padding: 12px;
                    position: relative;
                }
                .deal-featured-img {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: contain !important;
                    max-height: 220px !important;
                    aspect-ratio: auto !important;
                    transition: transform 0.3s;
                }
                .deal-featured-card:hover .deal-featured-img {
                    transform: scale(1.05);
                }
                .deal-tag-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }
                .deal-badge-flash {
                    background: #fee2e2;
                    color: #ef4444;
                    font-size: 11px;
                    font-weight: 800;
                    padding: 4px 10px;
                    border-radius: 9999px;
                    letter-spacing: 0.5px;
                }
                .deal-wishlist-btn {
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 6px;
                    border-radius: 50%;
                    transition: background 0.2s;
                }
                .deal-wishlist-btn:hover {
                    background: #f1f5f9;
                }
                .deal-delivery-badge {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #16a34a;
                    margin-bottom: 12px;
                }
                .deal-thunderbolt {
                    color: #22c55e;
                }
                .deal-featured-title {
                    font-size: 20px;
                    font-weight: 800;
                    color: #1e293b;
                    margin-bottom: 8px;
                    line-height: 1.3;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
                .deal-featured-desc {
                    font-size: 13px;
                    color: #64748b;
                    margin-bottom: 16px;
                    line-height: 1.5;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
                .deal-price-section {
                    display: flex;
                    align-items: baseline;
                    justify-content: space-between;
                    margin-bottom: 16px;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .deal-price-wrapper {
                    display: flex;
                    align-items: baseline;
                    gap: 8px;
                }
                .deal-price {
                    font-size: 24px;
                    font-weight: 800;
                    color: #ea580c;
                }
                .deal-mrp {
                    font-size: 14px;
                    text-decoration: line-through;
                    color: #94a3b8;
                }
                .deal-shop-name {
                    font-size: 12px;
                    color: #94a3b8;
                    font-weight: 600;
                }
                .deal-add-to-cart-btn {
                    background: #ea580c;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 24px;
                    font-weight: 700;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background 0.2s;
                    width: max-content;
                }
                .deal-add-to-cart-btn:hover {
                    background: #d97706;
                }

                /* ── Regular Cards Styling ── */
                .deal-regular-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f1f5f9;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.02);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .deal-regular-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                }
                .deal-card-img-wrapper {
                    position: relative;
                    height: 180px;
                    background: #f8fafc;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 12px;
                }
                .deal-card-img {
                    width: auto !important;
                    height: auto !important;
                    max-width: 100% !important;
                    max-height: 100% !important;
                    object-fit: contain !important;
                    aspect-ratio: auto !important;
                }
                .deal-badge-discount {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: #fee2e2;
                    color: #ef4444;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 4px 8px;
                    border-radius: 9999px;
                }
                .deal-card-wishlist {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    background: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                    cursor: pointer;
                }
                .deal-card-body {
                    padding: 16px;
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                    justify-content: space-between;
                }
                .deal-card-delivery {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #16a34a;
                    margin-bottom: 8px;
                }
                .deal-card-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 12px;
                    line-height: 1.4;
                    height: 40px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
                .deal-card-price-row {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    margin-bottom: 12px;
                }
                .deal-card-price {
                    font-size: 18px;
                    font-weight: 800;
                    color: #ea580c;
                }
                .deal-card-mrp {
                    font-size: 12px;
                    text-decoration: line-through;
                    color: #94a3b8;
                }
                .deal-card-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: auto;
                    border-top: 1px solid #f8fafc;
                    padding-top: 8px;
                }
                .deal-card-shop {
                    font-size: 11px;
                    color: #94a3b8;
                    font-weight: 600;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                    max-width: 110px;
                }
                .deal-card-add-btn {
                    background: #ea580c;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    padding: 6px 16px;
                    font-weight: 700;
                    font-size: 12px;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .deal-card-add-btn:hover {
                    background: #d97706;
                }

                /* ── Responsiveness ── */
                @media (max-width: 991px) {
                    .deal-hero-section {
                        flex-direction: column;
                        align-items: stretch;
                        padding: 24px;
                    }
                    .deal-timer-card {
                        align-self: center;
                        width: 100%;
                        max-width: 320px;
                    }
                    .deal-hero-illustration {
                        display: none !important;
                    }
                }
                @media (max-width: 767px) {
                    .deal-featured-card {
                        flex-direction: column;
                        padding: 16px;
                        gap: 16px;
                    }
                    .deal-featured-img-container {
                        height: 180px;
                    }
                    .deal-add-to-cart-btn {
                        width: 100%;
                    }
                    .deals-page {
                        padding-left: 10px;
                        padding-right: 10px;
                    }
                }
            `}</style>
        </>
    );
};

export default Deals;
